import { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import {
  MaterializedFormDocument,
  MaterializedFormFragment,
  TrainingClassesWithMaterializedFormsDocument,
} from 'generated/resources/graphql'

import SingleChoice from 'modules/core/components/Filter/filters/SingleChoice'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { AnswersTable } from 'modules/form_answers/components/AnswersTable'
import Loading from 'modules/layout/components/Page/Loading'
import { TrainingFormContextType } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/TrainingFormDetail/types'

const Answers = () => {
  const { trainingForm } = useOutletContext<TrainingFormContextType>()
  const isAnonymous = trainingForm?.anonymous
  const { data: trainingClassesWithForm, loading: loadingTrainingClasses } = useQuery(
    TrainingClassesWithMaterializedFormsDocument,
    {
      variables: {
        formId: trainingForm.id.toString(),
        trainingId: Number(trainingForm.resourceId),
      },
    }
  )
  const trainingClassesGroupOptions =
    trainingClassesWithForm?.trainings?.trainingClassesConnection.nodes.filter(
      (trainingClass) => trainingClass.materializedForms.length > 0
    ) || []

  const groupsOptions = trainingClassesGroupOptions.map((node) => ({
    label: node.name,
    value: Number(node.id),
  }))
  useEffect(() => {
    setSelectedGroup(groupsOptions[0]?.value)
  }, [groupsOptions[0]?.value])

  const [selectedGroup, setSelectedGroup] = useState<number>()

  const { data, loading: loadingAnswers } = useQuery(MaterializedFormDocument, {
    variables: {
      classIds: selectedGroup ? [selectedGroup] : undefined,
      formId: trainingForm.id.toString(),
      trainingId: Number(trainingForm.resourceId),
    },
  })

  const trainingClasses = data?.trainings?.trainingClassesConnection.edges || []

  const formData = trainingClasses.flatMap((edge) =>
    (edge.node.materializedForms || []).map((form) => ({
      ...form,
      trainingClassName: edge.node.name,
    }))
  )

  const formatQuestions = (forms: MaterializedFormFragment[]) =>
    forms.flatMap(
      (form) =>
        form.materializedQuestions?.map((question) => ({
          label: question.label,
          id: question.id,
          questionType: question.questionType,
          options: question.materializedQuestionOptions,
          mandatory: question.mandatory,
          maxChoices: question.maxChoices,
          position: question.position,
          scale:
            question.scale?.map((s) => ({
              value: String(s.value),
              text: s.text,
            })) || [],
        })) || []
    )
  const formatSections = (forms: MaterializedFormFragment[]) =>
    forms.flatMap(
      (form) =>
        form.materializedSections.map((section) => ({
          id: section.id,
          title: section.title ?? '',
          position: section.position,
          questions: section.materializedQuestions.map((question) => ({
            label: question.label,
            id: question.id,
            questionType: question.questionType,
            options: question.materializedQuestionOptions,
            mandatory: question.mandatory,
            maxChoices: question.maxChoices,
            position: question.position,
            scale:
              question.scale?.map((s) => ({
                value: String(s.value),
                text: s.text,
              })) || [],
          })),
        })) || []
    )

  const formattedSections = formatSections(formData)

  const sectionQuestionIds = new Set(
    formattedSections.flatMap((section) => section.questions.map((question) => question.id))
  )

  const formattedQuestions = formatQuestions(formData).filter(
    (question) => !sectionQuestionIds.has(question.id)
  )

  const participantsWithAnswers = formData.flatMap((form) => {
    // Shuffle the edges inline to randomize order
    const shuffledEdges = form.membershipsConnection.edges
      .map((edge) => ({ edge, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map(({ edge }) => edge)

    return shuffledEdges
      .filter((edge) => {
        const membership = edge.node
        const sectionQuestions = formattedSections.flatMap((section) => section.questions)
        const allQuestions = [...sectionQuestions, ...formattedQuestions]

        return allQuestions.some((question) => {
          const answer = membership.answers.find((a) => a.materializedQuestion?.id === question.id)
          return answer?.answer?.length || (answer?.materializedQuestionOption?.length ?? 0)
        })
      })
      .map((edge, index) => {
        const membership = edge.node

        const participant = trainingForm?.anonymous
          ? {
              id: index + 1,
              avatar: '',
              fullName: `Participant ${index + 1}`,
            }
          : membership.target || {
              id: index + 1,
              avatar: '',
              fullName: `Participant ${index + 1}`,
            }

        const sectionQuestions = formattedSections.flatMap((section) => section.questions)
        const allQuestions = [...sectionQuestions, ...formattedQuestions]

        const answers = allQuestions
          .map((question) => {
            const answer = membership.answers.find(
              (a) => a.materializedQuestion?.id === question.id
            )
            return {
              questionId: question.id,
              label: question.label,
              questionType: question.questionType,
              options: answer?.materializedQuestionOption ?? [],
              answer: answer?.answer ?? '',
              comment: answer?.comment,
            }
          })
          .filter((answer) => answer.answer.length || answer.options.length)

        return {
          respondent: {
            fullName: membership.respondent?.fullName,
            id: String(membership.respondent?.id),
          },
          targetId: membership.target?.id ? String(membership.target?.id) : null,
          answers,
          participant: {
            ...participant,
            id: participant.id,
            avatar: participant.avatar ?? '',
            trainingClassName: form.trainingClassName,
          },
        }
      })
  })

  const tableData = {
    questions: formattedQuestions,
    sections: formattedSections,
    participantsWithAnswers,
  }
  if (loadingTrainingClasses || loadingAnswers) return <Loading />
  return (
    <Box>
      <Box gap="s8" flexDirection="row" width="fit-content">
        <SingleChoice
          label={i18n.t('common.group')}
          options={groupsOptions}
          value={selectedGroup}
          onValueChange={(id) => setSelectedGroup(id)}
          noFooter
        />
      </Box>

      <AnswersTable
        formStatus={trainingForm.status}
        formType={trainingForm.formType}
        data={tableData}
        anonymous={isAnonymous}
      />
    </Box>
  )
}

export default Answers
